import React, { useState, useEffect } from 'react';
import { CLIENT_TYPE_OPTIONS, cn, YES_NO_OPTIONS, VALID_COUNTRIES } from '@/lib/utils';
import { usePage } from '@inertiajs/react';
import FormItem from '@/components/molecules/formItem';
import Heading from '@/components/atoms/heading';
import Text from '@/components/atoms/text';
import Separator from '@/components/atoms/separator';
// import Button from '@/components/atoms/button';
import { dropdownOptions } from '@/lib/utils';
import AddressLookup from '@/components/molecules/addressLookup';
import { axiosHandler } from '@/lib/utils/axiosHandler';
import Alert from '@/components/molecules/alert';
import Search from '../../Search/Search';
import { FormLabel, FormMessage } from '@/components/ui/form';

export default ({ data, errors, showAlert, handleOnChange, handleOnBlur, setData }) => {
	const { accountType } = usePage().props.auth;
	const { dropdowns, countries, auth } = usePage().props;
	const isEIS = accountType === 'eis';
	const isBroker = accountType === 'broker';
	const isGuest = accountType === 'guest';

	const titleDropdown = Object.keys(dropdowns?.title).map((key) => ({
		label: dropdowns?.title[key].key,
		value: dropdowns?.title[key].value,
	}));

	const [selectedValue, setSelectedValue] = useState(data.broker_id ? 'Broker' : '');
	const [brokerData, setBrokerData] = useState([]);
	const [introducerData, setIntroducerData] = useState([]);
	const [selectedLegaltitle, setSelectedLegaltitle] = useState(false);
	const [showTitle, setShowTitle] = useState(false);
	const [showFellowPartner, setShowFellowPartner] = useState(false);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const handleClientTypeChange = async (value) => {
		setSelectedValue(value);
		setData('client_type', value);

		if (value === 'Broker') {
			const responseData = await axiosHandler(auth, 'get', '/api/admin/get-brokers');
			if (responseData) {
				setBrokerData(responseData.brokers);
			}
		} else if (value === 'Introducer') {
			const responseData = await axiosHandler(auth, 'get', '/api/admin/get-introducers');
			if (responseData) {
				setIntroducerData(responseData.introducers);
			}
		}
	};

	return (
		<div className="flex flex-col gap-8">
			{isEIS && (
				<>
					<div className="flex flex-col items-start gap-8 md:flex-row">
						<div className="flex flex-col gap-2 md:gap-6 shrink-0 md:basis-[26.466%]">
							<Heading className="text-eis-black" type="h4">
								Client type
							</Heading>
							<Text type="p" variant="normal" className="text-formOutlineDark">
								Fields marked * are required to submit form
							</Text>
						</div>
						<div className="w-full space-y-8">
							<FormItem
								type="select"
								label="Client type"
								name="client_type"
								placeholder="Please select client type"
								options={CLIENT_TYPE_OPTIONS}
								value={selectedValue}
								onChange={handleClientTypeChange}
								meta={{
									msg: errors?.client_type,
									touched: errors?.client_type ? true : false,
								}}
							/>

							{selectedValue === 'Broker' && (
								<div className="flex flex-col w-full gap-4">
									<div className="flex flex-col gap-2">
										<FormLabel name="search_broker" className="flex gap-1">
											Broker
										</FormLabel>
									</div>
									<Search
										type="brokers"
										prefillValue={parseInt(data.broker_id)}
										value={parseInt(data.broker_id)}
										onClick={(e) => {
											setData((data) => ({ ...data, broker_id: e.id ? parseInt(e.id) : '' }));
										}}
										handleClearSearch={() => {
											setData((data) => ({ ...data, broker_id: '' }));
										}}
									/>
									<FormMessage
										name="broker_id"
										meta={{
											msg: errors?.broker_id,
											touched: errors?.broker_id ? true : false,
										}}
									/>
								</div>
							)}

							{selectedValue === 'Introducer' && (
								<FormItem
									type="select"
									label="Introducer"
									name="introducer"
									placeholder="Please select Introducer"
									options={introducerData.map((introducer) => ({
										label: introducer.name,
										value: introducer.id.toString(),
									}))}
									value={parseInt(data.introducer).toString()}
									onChange={(value) => setData('introducer', value)}
									meta={{
										msg: errors?.introducer,
										touched: errors?.introducer ? true : false,
									}}
								/>
							)}
						</div>
					</div>
					<Separator orientation="horizontal" />
				</>
			)}
			<div className="flex flex-col items-start gap-8 md:flex-row">
				<div className="flex flex-col gap-2 md:gap-6 shrink-0 md:basis-[26.466%]">
					<Heading className="text-eis-black" type="h4">
						Insured Policy Holder Details
					</Heading>
					<Text type="p" variant="normal" className="text-formOutlineDark">
						Fields marked * are required to submit form
					</Text>
				</div>
				<div className="w-full space-y-8">
					<FormItem
						type="radiogroup"
						name="age_check"
						label={isBroker ? `Is the Insured over the age of 16` : `Are you over the age of sixteen`}
						value={data.age_check}
						options={YES_NO_OPTIONS}
						validation="required"
						isHorizontal={true}
						onChange={(value) => handleOnChange('age_check', value)}
						meta={{
							msg: errors?.age_check,
							touched: errors?.age_check ? true : false,
						}}
					/>
					{data.age_check === 'no' && (
						<Alert
							type="bigger-icon"
							title="Sixteen or under"
							description="If you are sixteen or under we need to get consent from your parent or legal guardian to collect your information.
						Please contact the office in 01425 470360 to discuss completing an application form."
							className="alert-form-item"
						/>
					)}

					<FormItem
						type="radiogroup"
						name="is_business"
						label="Is the insurance to be in the name of an organisation"
						description="For example if you are a Limited Company, Committee, CIC etc…"
						value={data.is_business}
						options={YES_NO_OPTIONS}
						isHorizontal={true}
						onChange={(value) => handleOnChange('is_business', value)}
						validation="required"
						meta={{
							msg: errors?.is_business,
							touched: errors?.is_business ? true : false,
						}}
					/>

					{data.is_business === 'yes' && (
						<FormItem
							type="text"
							name="proposer_name"
							label="Organisation Name"
							placeholder="Organisation Name"
							description='Please include your legal title for example BC Events Committee", or "XYZ Events Limited" as it should appear on any documentation'
							value={data.proposer_name}
							onChange={(value) => handleOnChange('proposer_name', value)}
							onBlur={() => handleOnBlur('proposer_name')}
							autoComplete="proposer_name"
							validation="required"
							meta={{
								msg: errors?.proposer_name,
								touched: errors?.proposer_name ? true : false,
							}}
						/>
					)}

					<FormItem
						type="select"
						label={data.is_business === 'yes' ? 'Named Contact Title' : 'Title'}
						name="title"
						placeholder="Please select title"
						options={titleDropdown}
						value={data.title}
						onChange={(value) => setData('title', value)}
						meta={{
							msg: errors?.title,
							touched: errors?.title ? true : false,
						}}
					/>

					<FormItem
						type="text"
						name="first_name"
						label={data.is_business === 'yes' ? 'Named Contact First Name' : 'First Name'}
						placeholder="First name"
						value={data.first_name}
						onChange={(value) => handleOnChange('first_name', value)}
						onBlur={() => handleOnBlur('first_name')}
						autoComplete="first_name"
						validation="required"
						meta={{
							msg: errors?.first_name,
							touched: errors?.first_name ? true : false,
						}}
					/>

					<FormItem
						type="text"
						name="last_name"
						label={data.is_business === 'yes' ? 'Named Contact Surname' : 'Surname'}
						placeholder="Surname"
						value={data.last_name}
						onChange={(value) => handleOnChange('last_name', value)}
						onBlur={() => handleOnBlur('last_name')}
						autoComplete="last_name"
						validation="required"
						meta={{
							msg: errors?.last_name,
							touched: errors?.last_name ? true : false,
						}}
					/>
				</div>
			</div>
			<Separator orientation="horizontal" />
			<div className="flex flex-col items-start gap-8 md:flex-row">
				<div className="flex flex-col gap-2 md:gap-6 shrink-0 md:basis-[26.466%]">
					<Heading className="text-eis-black" type="h4">
						{isGuest ? 'Your address' : 'Clients address'}
					</Heading>
					<Text type="p" variant="normal" className="text-formOutlineDark">
						Fields marked * are required to submit form
					</Text>
				</div>
				<div className="w-full space-y-8">
					<AddressLookup
						postcodeField={{
							key: 'postcode_lookup',
							type: 'postcode_lookup',
							label: 'Postcode lookup',
						}}
						data={data}
						validation="required"
						setData={setData}
						errors={errors}
						defaultFullAddress={{
							address_1: data.address_1,
							address_2: data.address_2,
							town: data.town,
							county: data.county,
							postcode: data.postcode,
						}}
						postcodeData={data.postcode}
					/>

					<FormItem
						type="select"
						label="Country"
						name="country_id"
						validation="required"
						placeholder="Please select country"
						options={dropdownOptions(countries, 'name', 'key')}
						value={data.country}
						onChange={(value) => setData('country', value)}
						meta={{
							msg: errors?.country,
							touched: errors?.country ? true : false,
						}}
					/>
					{data.country && !VALID_COUNTRIES.includes(data.country) && (
						<Alert
							type="bigger-icon"
							title="UK Resident"
							description="Unfortunately, we are unable to supply cover if you are not a UK resident or registered company."
							className="alert-form-item"
						/>
					)}
				</div>
			</div>
			<Separator orientation="horizontal" />
			<div className="flex flex-col items-start gap-8 md:flex-row">
				<div className="flex flex-col gap-2 md:gap-6 shrink-0 md:basis-[26.466%]">
					<Heading className="text-eis-black" type="h4">
						{isGuest ? 'Your contact details' : 'Clients contact details'}
					</Heading>
					<Text type="p" variant="normal" className="text-formOutlineDark">
						Fields marked * are required to submit form
					</Text>
				</div>
				<div className="w-full space-y-8">
					<FormItem
						type="tel"
						name="telephone_num"
						label="Daytime telephone number"
						placeholder="Daytime telephone number"
						validation="required"
						value={data.telephone_num}
						onChange={(value) => handleOnChange('telephone_num', value)}
						onBlur={() => handleOnBlur('telephone_num')}
						autoComplete="telephone_num"
						meta={{
							msg: errors?.telephone_num,
							touched: errors?.telephone_num ? true : false,
						}}
					/>

					<FormItem
						type="email"
						name="email"
						label="Email address"
						placeholder="Email"
						value={data.email}
						onChange={(value) => handleOnChange('email', value)}
						onBlur={() => handleOnBlur('email')}
						autoComplete="email"
						validation="required"
						meta={{
							msg: errors?.email,
							touched: errors?.email ? true : false,
						}}
					/>
				</div>
			</div>
		</div>
	);
};
